/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import update from "immutability-helper"
import {
  AxiosAction,
  FailedResponse,
} from "order-manager/types/api/common.type"
import { createReducer } from "store/create-reducer"
import { getApiUrl } from "constants/domain"
import { FetchOrderListResponse } from "order-manager/types/api/orderFetchOrderList.type"
import { OrderPageType, OrderState } from "order-manager/constants/orderState"
import { DEFAULT_PAGE_SIZE } from "order-manager/constants/api"
import {
  FETCH_HISTORY_LIST,
  FETCH_ORDER_LIST,
} from "order-manager/constants/action"
import { FetchHistoryListResponse } from "order-manager/types/api/orderFetchHistoryList.type"
import { OrderSummaryResponse } from "order-manager/types/api/orderFetchSummary.type"
import { OrderNotification } from "order-manager/types/models/notification"
import { OrderEtaMap } from "order-manager/types/models/order_eta"
import { OrderCancelReasons } from "order-manager/types/models/order_cancel_reasons"
import { OrderEtaResponse } from "order-manager/types/api/orderFetchEta.type"
import { FetchOrderByIDResponse } from "order-manager/types/api/orderFetchOrderByID.type"
import { FetchCancelReasons } from "order-manager/types/api/orderFetchCancelReasons.type"
import {
  markOrderRequest,
  markOrderAsDeliveringOrDeliveredRequest,
} from "order-manager/types/api/orderMark.type"
import { getOrderManagerStorageConfig } from "order-manager/utils/storage_config_helper"
import { OrderManagerStorageConfig } from "order-manager/types/models/storage_config"
import { OpenedOrderDetail } from "order-manager/types/models/opened_order"
import { markStatus } from "order-manager/constants/mark"
import {
  isEnableFallbackNotification,
  checkNewOrder,
} from "order-manager/utils/notification_helper"
import {
  ReviewOrderChangesRequest,
  ReviewOrderChangesResponse,
} from "order-manager/types/api/reviewOrderChanges.type"
import { CancelOrderSubmissionOutcome, OrderManagerState } from "./order.type"

const NAMESPACE = "ORDER_MANAGER"

// file constant
const apiUrl = getApiUrl()
const actionSuccess = (action: string) => `${action}_SUCCESS`
const actionFail = (action: string) => `${action}_FAIL`

function getCancelOrderRequestMeta(
  previousAction: unknown,
): {
  orderID: string
  cancelCode?: number
} {
  const pa = previousAction as {
    payload?: { request?: { data?: Record<string, unknown> } }
  }
  const data = pa?.payload?.request?.data
  const orderID = typeof data?.orderID === "string" ? data.orderID : ""
  const cancelCode =
    typeof data?.cancelCode === "number" ? data.cancelCode : undefined
  return { orderID, cancelCode }
}

// actions
export const actions = {
  RESET_ORDER_LIST: `${NAMESPACE}/RESET_ORDER_LIST`,
  FETCH_ORDER_LIST,
  FETCH_ORDER_BY_ID: `${NAMESPACE}/FETCH_ORDER_BY_ID`,
  FETCH_HISTORY_LIST,
  FETCH_ORDER_SUMMARY: `${NAMESPACE}/FETCH_ORDER_SUMMARY`,
  FETCH_ORDER_ETA: `${NAMESPACE}/FETCH_ORDER_ETA`,
  FETCH_ORDER_EXPORTED_FILE: `${NAMESPACE}/FETCH_ORDER_EXPORTED_FILE`,
  FETCH_ORDER_EXPORTED_MPA_FILE: `${NAMESPACE}/FETCH_ORDER_EXPORTED_MPA_FILE`,
  FETCH_ORDER_CANCEL_REASONS: `${NAMESPACE}/FETCH_ORDER_CANCEL_REASONS`,
  MARK_ORDER: `${NAMESPACE}/MARK_ORDER`,
  UPDATE_WAITING_MARK_ORDER: `${NAMESPACE}/UPDATE_WAITING_MARK_ORDER`,
  NEW_ORDER_NOTIFICATION: `${NAMESPACE}/NEW_ORDER_NOTIFICATION`,
  CHANGE_ORDER_NOTIFICATION_COUNT: `${NAMESPACE}/CHANGE_ORDER_NOTIFICATION_COUNT`,
  REMOVE_ORDER_FROM_NOTIFICATION_LIST: `${NAMESPACE}/REMOVE_ORDER_FROM_NOTIFICATION_LIST`,
  UPDATE_ORDER_MANAGER_CONFIG: `${NAMESPACE}/UPDATE_ORDER_MANAGER_CONFIG`,
  UPDATE_OPENED_ORDER_DETAIL: `${NAMESPACE}/UPDATE_OPENED_ORDER_DETAIL`,
  CANCEL_ORDER: `${NAMESPACE}/CANCEL_ORDER`,
  REVIEW_ORDER_CHANGES: `${NAMESPACE}/REVIEW_ORDER_CHANGES`,
  SUBMIT_ORDER_CHANGES: `${NAMESPACE}/SUBMIT_ORDER_CHANGES`,
  CLEAR_CANCEL_ORDER_SUBMISSION_OUTCOME: `${NAMESPACE}/CLEAR_CANCEL_ORDER_SUBMISSION_OUTCOME`,
}

type ActionTypes =
  | typeof actions.RESET_ORDER_LIST
  | typeof actions.FETCH_ORDER_LIST
  | typeof actions.FETCH_ORDER_BY_ID
  | typeof actions.FETCH_HISTORY_LIST
  | typeof actions.FETCH_ORDER_SUMMARY
  | typeof actions.FETCH_ORDER_ETA
  | typeof actions.MARK_ORDER
  | typeof actions.UPDATE_WAITING_MARK_ORDER
  | typeof actions.NEW_ORDER_NOTIFICATION
  | typeof actions.CHANGE_ORDER_NOTIFICATION_COUNT
  | typeof actions.REMOVE_ORDER_FROM_NOTIFICATION_LIST
  | typeof actions.UPDATE_ORDER_MANAGER_CONFIG
  | typeof actions.UPDATE_OPENED_ORDER_DETAIL
  | typeof actions.CANCEL_ORDER
  | typeof actions.FETCH_ORDER_CANCEL_REASONS
  | typeof actions.FETCH_ORDER_EXPORTED_FILE
  | typeof actions.FETCH_ORDER_EXPORTED_MPA_FILE
  | typeof actions.REVIEW_ORDER_CHANGES
  | typeof actions.SUBMIT_ORDER_CHANGES
  | typeof actions.CLEAR_CANCEL_ORDER_SUBMISSION_OUTCOME

// action creators
const resetOrderList = () => ({
  type: actions.RESET_ORDER_LIST,
})

const fetchOrderByID = ({
  orderID,
  merchantID,
}: {
  orderID: string
  merchantID: string
}) => ({
  type: actions.FETCH_ORDER_BY_ID,
  payload: {
    orderID,
    merchantID,
    showGlobalLoading: false,
    showError: true,
    request: {
      url: `${apiUrl}/food/merchant/v3/orders/${orderID}`,
      method: "get",
      headers: {
        merchantID,
      },
    },
  },
})

const fetchOrderList = ({
  merchantID,
  pageType,
  autoAcceptGroup,
  searchToken,
  isLoadMore = false,
  size = DEFAULT_PAGE_SIZE,
  append = false,
}: {
  merchantID: string
  pageType: string
  autoAcceptGroup: number
  searchToken: string
  isLoadMore?: boolean
  size?: number
  append?: boolean
}) => ({
  type: actions.FETCH_ORDER_LIST,
  showGlobalLoading: false,
  showError: true,
  payload: {
    append,
    pageType,
    isLoadMore,
    request: {
      url: `${apiUrl}/delvplatformapi/merchant/v4/orders-pagination?AutoAcceptGroup=${autoAcceptGroup}&merchantID=${merchantID}&PageType=${pageType}&searchToken=${searchToken}&size=${size}`,
      method: "get",
      headers: {
        merchantID,
      },
    },
  },
})

const fetchHistoryList = ({
  merchantID,
  startTime,
  endTime,
  pageIndex,
  states = [],
  isLoadMore = false,
  pageSize = DEFAULT_PAGE_SIZE,
  append = false,
}: {
  merchantID: string
  startTime: string
  endTime: string
  pageIndex: number
  states?: string[]
  isLoadMore?: boolean
  pageSize?: number
  append?: boolean
}) => ({
  type: actions.FETCH_HISTORY_LIST,
  showGlobalLoading: false,
  showError: true,
  payload: {
    append,
    isLoadMore,
    startTime,
    endTime,
    states,
    request: {
      url: `${apiUrl}/delvplatformapi/merchant/v1/reports/daily-pagination?states=${states}&startTime=${startTime}&endTime=${endTime}&pageIndex=${pageIndex}&pageSize=${pageSize}`,
      method: "get",
      headers: {
        merchantID,
      },
    },
  },
})

const fetchOrderSummary = ({
  merchantID,
  startDate,
}: {
  merchantID: string
  startDate: string
}) => ({
  type: actions.FETCH_ORDER_SUMMARY,
  showGlobalLoading: false,
  showError: true,
  payload: {
    request: {
      url: `${apiUrl}/food/merchant/v1/merchant-report-summary?startDate=${startDate}`,
      method: "get",
      headers: {
        merchantID,
      },
    },
  },
})

const fetchOrderEta = ({
  merchantID,
  orderIDs,
  orderState,
  resetMap,
}: {
  merchantID: string
  orderIDs: string
  orderState: OrderState
  resetMap: boolean
}) => ({
  type: actions.FETCH_ORDER_ETA,
  showGlobalLoading: false,
  showError: true,
  payload: {
    orderState,
    resetMap,
    request: {
      url: `${apiUrl}/food/merchant/v2/orders-eta?OrderIDs=${orderIDs}`,
      method: "get",
      headers: {
        merchantID,
      },
    },
  },
})

const fetchOrderCancelReasons = ({
  merchantID,
  queryParams,
}: {
  merchantID: string
  queryParams: {
    orderID: string
    locale: string
  }
}) => ({
  type: actions.FETCH_ORDER_CANCEL_REASONS,
  payload: {
    request: {
      url: `${apiUrl}/food/merchant/v1/order-unchangeable-reasons`,
      method: "get",
      params: {
        ...queryParams,
      },
      headers: {
        merchantID,
      },
    },
  },
})

const fetchOrderExportedFile = ({
  payload,
  merchantID,
}: {
  payload: {
    orderIDs: string[]
  }
  merchantID: string
}) => ({
  type: actions.FETCH_ORDER_EXPORTED_FILE,
  payload: {
    request: {
      url: `${apiUrl}/delvplatformapi/merchant/v1/mexsupplies/invoice/export`,
      method: "post",
      data: payload,
      headers: {
        merchantID,
      },
    },
  },
})

const fetchOrderExportedMpaFile = ({
  payload,
  merchantID,
}: {
  payload: {
    startTime: string
    endTime: string
  }
  merchantID: string
}) => ({
  type: actions.FETCH_ORDER_EXPORTED_MPA_FILE,
  payload: {
    request: {
      url: `${apiUrl}/delvplatformapi/merchant/v1/mexsupplies/mpatransaction/export`,
      method: "post",
      data: payload,
      headers: {
        merchantID,
      },
    },
  },
})

const cancelOrder = ({
  merchantID,
  queryParams,
}: {
  merchantID: string
  queryParams: {
    orderID: string
    cancelReason: string
    cancelCode: number
  }
}) => ({
  type: actions.CANCEL_ORDER,
  payload: {
    request: {
      url: `${apiUrl}/food/merchant/v1/cancel-order/${queryParams?.orderID}`,
      method: "put",
      data: queryParams,
      headers: {
        merchantID,
      },
    },
  },
})

const markOrder = ({
  merchantID,
  data,
  showGlobalLoading = true,
  status,
}: {
  merchantID: string
  data: markOrderRequest | markOrderAsDeliveringOrDeliveredRequest
  showGlobalLoading: boolean
  status?: number
}) => {
  let url
  if (status === markStatus.Delivering) {
    url = `${apiUrl}/food/merchant/v1/deliver-by-mex`
  } else if (status === markStatus.Delivered) {
    url = `${apiUrl}/food/merchant/v3/orders/${
      (data as markOrderAsDeliveringOrDeliveredRequest)?.orderID
    }/terminate`
  } else {
    url = `${apiUrl}/food/merchant/orders/mark`
  }

  return {
    type: actions.MARK_ORDER,
    showGlobalLoading,
    showError: true,
    payload: {
      request: {
        url,
        method: "post",
        headers: {
          merchantID,
        },
        data,
      },
    },
  }
}

const reviewOrderChanges = ({
  orderID,
  merchantID,
  data,
}: {
  orderID: string
  merchantID: string
  data: ReviewOrderChangesRequest
}) => ({
  type: actions.REVIEW_ORDER_CHANGES,
  showGlobalLoading: true,
  showError: true,
  payload: {
    request: {
      url: `${apiUrl}/food/merchant/v1/review-order-changes/${orderID}`,
      method: "post",
      data,
      headers: {
        merchantID,
      },
    },
  },
})

const submitOrderChanges = ({
  orderSignatureID,
  orderID,
  merchantID,
}: {
  orderSignatureID: string
  orderID: string
  merchantID: string
}) => ({
  type: actions.SUBMIT_ORDER_CHANGES,
  showGlobalLoading: true,
  showError: true,
  payload: {
    request: {
      url: `${apiUrl}/food/merchant/v1/submit-order-changes/${orderID}`,
      method: "put",
      data: {
        orderSignatureID,
        orderID,
      },
      headers: {
        merchantID,
      },
    },
  },
})

const updateWaitingMarkOrder = (wait: boolean) => ({
  type: actions.UPDATE_WAITING_MARK_ORDER,
  payload: {
    wait,
  },
})

const newOrderNotification = ({ data }: { data: OrderNotification }) => ({
  type: actions.NEW_ORDER_NOTIFICATION,
  payload: {
    data,
  },
})

const changeOrderNotificationCount = (count: number) => ({
  type: actions.CHANGE_ORDER_NOTIFICATION_COUNT,
  payload: {
    count,
  },
})

const removeOrderFromNotificationList = (orderID: string) => ({
  type: actions.REMOVE_ORDER_FROM_NOTIFICATION_LIST,
  payload: {
    orderID,
  },
})

const updateOrderManagerConfig = (config: OrderManagerStorageConfig) => ({
  type: actions.UPDATE_ORDER_MANAGER_CONFIG,
  payload: {
    config,
  },
})

const updateOpenedOrderDetail = (openedOrderDetail: OpenedOrderDetail) => ({
  type: actions.UPDATE_OPENED_ORDER_DETAIL,
  payload: {
    openedOrderDetail,
  },
})

const mockAction = (
  actionType: string,
  data: any,
  meta: any,
  error: any = {},
) => ({
  type: actionType,
  payload: data,
  meta,
  error,
})

const clearCancelOrderSubmissionOutcome = () => ({
  type: actions.CLEAR_CANCEL_ORDER_SUBMISSION_OUTCOME,
})

export const actionCreator = {
  resetOrderList,
  fetchOrderByID,
  fetchOrderList,
  fetchHistoryList,
  fetchOrderSummary,
  fetchOrderEta,
  fetchOrderCancelReasons,
  fetchOrderExportedFile,
  fetchOrderExportedMpaFile,
  cancelOrder,
  markOrder,
  updateWaitingMarkOrder,
  newOrderNotification,
  changeOrderNotificationCount,
  removeOrderFromNotificationList,
  updateOrderManagerConfig,
  updateOpenedOrderDetail,
  reviewOrderChanges,
  submitOrderChanges,
  clearCancelOrderSubmissionOutcome,
  mockAction,
}

type ActionCreatorTypes = {
  [K in keyof typeof actionCreator]: ReturnType<typeof actionCreator[K]>
}
export type ActionCreatorTypeList = ActionCreatorTypes[keyof ActionCreatorTypes]

// state
export const initialState = (): OrderManagerState => ({
  order: undefined,
  orderListUpcoming: {},
  orderListReady: {},
  orderListPreparing: {},
  orderListDelivering: {},
  orderHistoryList: {},
  orderHistorySelectedDate: {},
  orderSummary: {},
  orderPreparingEta: {} as OrderEtaMap,
  orderReadyEta: {} as OrderEtaMap,
  orderCancelReasons: {} as OrderCancelReasons,
  isFetchingOrder: false,
  newOrderNotification: {},
  orderNotificationCount: 0,
  orderNotificationList: [],
  orderManagerConfig: getOrderManagerStorageConfig(),
  openedOrderDetail: {
    preparingAndReadyOrDelivering: {} as { [key: string]: boolean },
    upcoming: {} as { [key: string]: boolean },
  },
  reviewOrderChangesInfo: {} as ReviewOrderChangesResponse,
  submitOrderChangesInfo: undefined,
  isFetchingOrderListUpcoming: true, // prevent blinking in UI first load
  isFetchingOrderListReady: true, // prevent blinking in UI first load
  isFetchingOrderListPreparing: true, // prevent blinking in UI first load
  isFetchingOrderListDelivering: true, // prevent blinking in UI first load
  isFetchingOrderHistoryList: true, // prevent blinking in UI first load
  isLoadMorePreparing: false,
  isLoadMoreDelivering: false,
  isLoadMoreReady: false,
  isLoadMoreUpcoming: false,
  isLoadMoreHistory: false,
  isFetchingOrderSummary: false,
  isFetchingOrderPreparingEta: false,
  isFetchingOrderReadyEta: false,
  isFetchingOrderCancelReasons: false,
  isMarkingOrder: false,
  isWaitingMarkOrder: false,
  isCancelingOrder: false,
  cancelOrderSubmissionOutcome: null,
  isReviewingOrder: false,
  isSubmittingOrderChanges: false,
  showReviewBeforeSubmitView: false,
})

// reducer
export const reducer = createReducer<
  OrderManagerState,
  ActionTypes,
  ActionCreatorTypeList
>(initialState(), {
  [actions.RESET_ORDER_LIST]: () => {
    return initialState()
  },
  [actions.FETCH_ORDER_BY_ID]: (state: OrderManagerState) => {
    return update(state, {
      order: { $set: undefined },
      isFetchingOrder: { $set: true },
    })
  },
  [actionSuccess(actions.FETCH_ORDER_BY_ID)]: (
    state: OrderManagerState,
    action: AxiosAction<
      FetchOrderByIDResponse,
      ActionCreatorTypes["fetchOrderByID"]
    >,
  ) => {
    const { order } = action?.payload?.data

    return update(state, {
      order: {
        $set: order,
      },
      isFetchingOrder: { $set: false },
    })
  },
  [actionFail(actions.FETCH_ORDER_BY_ID)]: (state: OrderManagerState) => {
    return update(state, {
      isFetchingOrder: { $set: false },
    })
  },
  [actions.FETCH_ORDER_LIST]: (
    state: OrderManagerState,
    action: ActionCreatorTypes["fetchOrderList"],
  ) => {
    if (action.payload.pageType === OrderPageType.PREPARINGV2) {
      return update(state, {
        isFetchingOrderListPreparing: { $set: !action.payload.isLoadMore }, // false if isLoadMore equal true
        isLoadMorePreparing: { $set: action.payload.isLoadMore },
      })
    }

    if (action.payload.pageType === OrderPageType.READY) {
      return update(state, {
        isFetchingOrderListReady: { $set: !action.payload.isLoadMore }, // false if isLoadMore equal true
        isLoadMoreReady: { $set: action.payload.isLoadMore },
      })
    }

    if (action.payload.pageType === OrderPageType.DELIVERING) {
      return update(state, {
        isFetchingOrderListDelivering: { $set: !action.payload.isLoadMore }, // false if isLoadMore equal true
        isLoadMoreDelivering: { $set: action.payload.isLoadMore },
      })
    }

    if (action.payload.pageType === OrderPageType.UPCOMING) {
      return update(state, {
        isFetchingOrderListUpcoming: { $set: !action.payload.isLoadMore }, // false if isLoadMore equal true
        isLoadMoreUpcoming: { $set: action.payload.isLoadMore },
      })
    }

    return state
  },
  [actionSuccess(actions.FETCH_ORDER_LIST)]: (
    state: OrderManagerState,
    action: AxiosAction<
      FetchOrderListResponse,
      ActionCreatorTypes["fetchOrderList"]
    >,
  ) => {
    const {
      orderStats,
      nextRequestTimestamp,
      hasMore,
      orders = [],
      pollInterval,
      nextSearchToken,
    } = action?.payload?.data

    if (
      action.meta.previousAction.payload.pageType === OrderPageType.PREPARINGV2
    ) {
      let newState = state

      if (isEnableFallbackNotification()) {
        const [isNewOrder, notif] = checkNewOrder(
          state.orderListPreparing?.orders,
          orders,
          action.meta.previousAction.payload.request.headers.merchantID,
        )

        if (isNewOrder) {
          notif.skipRefreshOrderList = true
          newState = update(newState, {
            newOrderNotification: { $set: notif },
            orderNotificationList: { $push: [notif] },
            orderNotificationCount: { $set: state.orderNotificationCount + 1 },
          })
        }
      }

      return update(newState, {
        orderListPreparing: {
          orderStats: { $set: orderStats },
          nextRequestTimestamp: { $set: nextRequestTimestamp },
          hasMore: { $set: hasMore || false },
          orders: action.meta.previousAction.payload.append
            ? { $push: orders }
            : { $set: orders },
          pollInterval: { $set: pollInterval },
          nextSearchToken: { $set: nextSearchToken },
        },
        isFetchingOrderListPreparing: { $set: false },
        isLoadMorePreparing: { $set: false },
      })
    }

    if (action.meta.previousAction.payload.pageType === OrderPageType.READY) {
      return update(state, {
        orderListReady: {
          orderStats: { $set: orderStats },
          nextRequestTimestamp: { $set: nextRequestTimestamp },
          hasMore: { $set: hasMore || false },
          orders: action.meta.previousAction.payload.append
            ? { $push: orders }
            : { $set: orders },
          pollInterval: { $set: pollInterval },
          nextSearchToken: { $set: nextSearchToken },
        },
        isFetchingOrderListReady: { $set: false },
        isLoadMoreReady: { $set: false },
      })
    }

    if (
      action.meta.previousAction.payload.pageType === OrderPageType.DELIVERING
    ) {
      return update(state, {
        orderListDelivering: {
          orderStats: { $set: orderStats },
          nextRequestTimestamp: { $set: nextRequestTimestamp },
          hasMore: { $set: hasMore || false },
          orders: action.meta.previousAction.payload.append
            ? { $push: orders }
            : { $set: orders },
          pollInterval: { $set: pollInterval },
          nextSearchToken: { $set: nextSearchToken },
        },
        isFetchingOrderListDelivering: { $set: false },
        isLoadMoreDelivering: { $set: false },
      })
    }

    if (
      action.meta.previousAction.payload.pageType === OrderPageType.UPCOMING
    ) {
      return update(state, {
        orderListUpcoming: {
          orderStats: { $set: orderStats },
          nextRequestTimestamp: { $set: nextRequestTimestamp },
          hasMore: { $set: hasMore || false },
          orders: action.meta.previousAction.payload.append
            ? { $push: orders }
            : { $set: orders },
          pollInterval: { $set: pollInterval },
          nextSearchToken: { $set: nextSearchToken },
        },
        isFetchingOrderListUpcoming: { $set: false },
        isLoadMoreUpcoming: { $set: false },
      })
    }

    return state
  },
  [actionFail(actions.FETCH_ORDER_LIST)]: (
    state: OrderManagerState,
    action: AxiosAction<
      FetchOrderListResponse,
      ActionCreatorTypes["fetchOrderList"]
    >,
  ) => {
    if (
      action.meta.previousAction.payload.pageType === OrderPageType.PREPARINGV2
    ) {
      return update(state, {
        isFetchingOrderListPreparing: { $set: false },
        isLoadMorePreparing: { $set: false },
      })
    }

    if (action.meta.previousAction.payload.pageType === OrderPageType.READY) {
      return update(state, {
        isFetchingOrderListReady: { $set: false },
        isLoadMoreReady: { $set: false },
      })
    }

    if (
      action.meta.previousAction.payload.pageType === OrderPageType.DELIVERING
    ) {
      return update(state, {
        isFetchingOrderListDelivering: { $set: false },
        isLoadMoreDelivering: { $set: false },
      })
    }

    if (
      action.meta.previousAction.payload.pageType === OrderPageType.UPCOMING
    ) {
      return update(state, {
        isFetchingOrderListUpcoming: { $set: false },
        isLoadMoreUpcoming: { $set: false },
      })
    }

    return state
  },
  [actions.FETCH_HISTORY_LIST]: (
    state: OrderManagerState,
    action: ActionCreatorTypes["fetchHistoryList"],
  ) => {
    return update(state, {
      orderHistoryList: {
        $set: action.payload.append ? state.orderHistoryList : {},
      },
      orderHistorySelectedDate: {
        $set: {
          startTime: action.payload.startTime,
          endTime: action.payload.endTime,
        },
      },
      orderHistorySelectedStates: {
        $set: action.payload.states,
      },
      isFetchingOrderHistoryList: { $set: !action.payload.isLoadMore },
      isLoadMoreHistory: { $set: action.payload.isLoadMore },
    })
  },
  [actionSuccess(actions.FETCH_HISTORY_LIST)]: (
    state: OrderManagerState,
    action: AxiosAction<
      FetchHistoryListResponse,
      ActionCreatorTypes["fetchHistoryList"]
    >,
  ) => {
    const {
      hasMore,
      pageIndex,
      pageEndTime,
      statements,
      pageSize,
    } = action?.payload?.data

    return update(state, {
      orderHistoryList: {
        hasMore: { $set: hasMore },
        pageIndex: { $set: pageIndex },
        pageEndTime: { $set: pageEndTime },
        statements: action.meta.previousAction.payload.append
          ? { $push: statements }
          : { $set: statements },
        pageSize: { $set: pageSize },
      },
      isFetchingOrderHistoryList: { $set: false },
      isLoadMoreHistory: { $set: false },
    })
  },
  [actionFail(actions.FETCH_HISTORY_LIST)]: (
    state: OrderManagerState,
    action: AxiosAction<FailedResponse>,
  ) => {
    return update(state, {
      orderHistoryList: {
        error: { $set: action?.payload?.data },
      },
      isFetchingOrderHistoryList: { $set: false },
      isLoadMoreHistory: { $set: false },
    })
  },
  [actions.FETCH_ORDER_SUMMARY]: (state: OrderManagerState) => {
    return update(state, {
      orderSummary: {
        $set: {},
      },
      isFetchingOrderSummary: { $set: true },
    })
  },
  [actionSuccess(actions.FETCH_ORDER_SUMMARY)]: (
    state: OrderManagerState,
    action: AxiosAction<
      OrderSummaryResponse,
      ActionCreatorTypes["fetchOrderSummary"]
    >,
  ) => {
    return update(state, {
      orderSummary: {
        $set: action?.payload?.data,
      },
      isFetchingOrderSummary: {
        $set: false,
      },
    })
  },
  [actionFail(actions.FETCH_ORDER_SUMMARY)]: (state: OrderManagerState) => {
    return update(state, {
      orderSummary: {
        $set: {},
      },
      isFetchingOrderSummary: {
        $set: false,
      },
    })
  },
  [actions.FETCH_ORDER_ETA]: (
    state: OrderManagerState,
    action: ActionCreatorTypes["fetchOrderEta"],
  ) => {
    if (
      action.payload.resetMap &&
      action.payload.orderState === OrderState.PREPARE
    ) {
      return update(state, {
        orderPreparingEta: {
          $set: {} as OrderEtaMap,
        },
        isFetchingOrderPreparingEta: { $set: true },
      })
    }

    if (
      action.payload.resetMap &&
      action.payload.orderState === OrderState.READY
    ) {
      return update(state, {
        orderReadyEta: {
          $set: {} as OrderEtaMap,
        },
        isFetchingOrderReadyEta: { $set: true },
      })
    }

    if (action.payload.orderState === OrderState.PREPARE) {
      return update(state, {
        isFetchingOrderPreparingEta: { $set: true },
      })
    }

    if (action.payload.orderState === OrderState.READY) {
      return update(state, {
        isFetchingOrderReadyEta: { $set: true },
      })
    }

    return state
  },
  [actionSuccess(actions.FETCH_ORDER_ETA)]: (
    state: OrderManagerState,
    action: AxiosAction<OrderEtaResponse, ActionCreatorTypes["fetchOrderEta"]>,
  ) => {
    const { pickingUpEta } = action?.payload?.data
    const etaTemp = {} as OrderEtaMap

    pickingUpEta?.map(eta => {
      etaTemp[eta.orderID] = eta
      return etaTemp
    })

    if (action.meta.previousAction.payload.orderState === OrderState.PREPARE) {
      return update(state, {
        orderPreparingEta: {
          $merge: etaTemp,
        },
        isFetchingOrderPreparingEta: {
          $set: false,
        },
      })
    }

    if (action.meta.previousAction.payload.orderState === OrderState.READY) {
      return update(state, {
        orderReadyEta: {
          $merge: etaTemp,
        },
        isFetchingOrderReadyEta: {
          $set: false,
        },
      })
    }

    return state
  },
  [actionFail(actions.FETCH_ORDER_ETA)]: (
    state: OrderManagerState,
    action: AxiosAction<OrderEtaResponse, ActionCreatorTypes["fetchOrderEta"]>,
  ) => {
    if (action.meta.previousAction.payload.orderState === OrderState.PREPARE) {
      return update(state, {
        isFetchingOrderPreparingEta: {
          $set: false,
        },
      })
    }

    if (action.meta.previousAction.payload.orderState === OrderState.READY) {
      return update(state, {
        isFetchingOrderReadyEta: {
          $set: false,
        },
      })
    }

    return state
  },
  [actions.FETCH_ORDER_CANCEL_REASONS]: (state: OrderManagerState) => {
    return update(state, {
      orderCancelReasons: {
        $set: {} as OrderCancelReasons,
      },
      isFetchingOrderCancelReasons: { $set: true },
    })
  },
  [actionSuccess(actions.FETCH_ORDER_CANCEL_REASONS)]: (
    state: OrderManagerState,
    action: AxiosAction<
      FetchCancelReasons,
      ActionCreatorTypes["fetchOrderCancelReasons"]
    >,
  ) => {
    const { data } = action?.payload
    return update(state, {
      orderCancelReasons: {
        $set: data,
      },
      isFetchingOrderCancelReasons: { $set: false },
    })
  },
  [actionFail(actions.FETCH_ORDER_CANCEL_REASONS)]: (
    state: OrderManagerState,
  ) => {
    return update(state, {
      isFetchingOrderCancelReasons: { $set: false },
    })
  },
  [actions.CANCEL_ORDER]: (state: OrderManagerState) => {
    return update(state, {
      isCancelingOrder: { $set: true },
      cancelOrderSubmissionOutcome: { $set: null },
    })
  },
  [actionSuccess(actions.CANCEL_ORDER)]: (
    state: OrderManagerState,
    action: AxiosAction<unknown, ActionCreatorTypes["cancelOrder"]>,
  ) => {
    const { orderID, cancelCode } = getCancelOrderRequestMeta(
      action.meta?.previousAction,
    )
    const outcome: CancelOrderSubmissionOutcome = {
      orderID,
      success: true,
      errorMessage: "",
      ...(cancelCode !== undefined ? { cancelCode } : {}),
    }
    return update(state, {
      isCancelingOrder: { $set: false },
      cancelOrderSubmissionOutcome: { $set: outcome },
    })
  },
  [actionFail(actions.CANCEL_ORDER)]: (
    state: OrderManagerState,
    action: AxiosAction<FailedResponse, ActionCreatorTypes["cancelOrder"]>,
  ) => {
    const { orderID, cancelCode } = getCancelOrderRequestMeta(
      action.meta?.previousAction,
    )
    const apiMessage = action.error?.response?.data?.message
    const errorMessage =
      typeof apiMessage === "string" && apiMessage.trim() !== ""
        ? apiMessage
        : "CANCEL_FAILED"
    const outcome: CancelOrderSubmissionOutcome = {
      orderID,
      success: false,
      errorMessage,
      ...(cancelCode !== undefined ? { cancelCode } : {}),
    }
    return update(state, {
      isCancelingOrder: { $set: false },
      cancelOrderSubmissionOutcome: { $set: outcome },
    })
  },
  [actions.CLEAR_CANCEL_ORDER_SUBMISSION_OUTCOME]: (
    state: OrderManagerState,
  ) => {
    return update(state, {
      cancelOrderSubmissionOutcome: { $set: null },
    })
  },
  [actions.REVIEW_ORDER_CHANGES]: (state: OrderManagerState) => {
    return update(state, {
      isReviewingOrder: { $set: true },
      reviewOrderChangesInfo: { $set: {} as ReviewOrderChangesResponse },
      showReviewBeforeSubmitView: { $set: false },
    })
  },
  [actionSuccess(actions.REVIEW_ORDER_CHANGES)]: (
    state: OrderManagerState,
    action: AxiosAction<
      ReviewOrderChangesResponse,
      ActionCreatorTypes["reviewOrderChanges"]
    >,
  ) => {
    return update(state, {
      reviewOrderChangesInfo: { $set: action?.payload?.data },
      isReviewingOrder: { $set: false },
      showReviewBeforeSubmitView: { $set: true },
    })
  },
  [actionFail(actions.REVIEW_ORDER_CHANGES)]: (
    state: OrderManagerState,
    action: AxiosAction<
      ReviewOrderChangesResponse,
      ActionCreatorTypes["reviewOrderChanges"]
    >,
  ) => {
    return update(state, {
      isReviewingOrder: { $set: false },
      reviewOrderChangesInfo: {
        error: { $set: action?.error?.response?.data },
      },
    })
  },
  [actions.SUBMIT_ORDER_CHANGES]: (state: OrderManagerState) => {
    return update(state, {
      isSubmittingOrderChanges: { $set: true },
      submitOrderChangesInfo: { $set: undefined },
    })
  },
  [actionSuccess(actions.SUBMIT_ORDER_CHANGES)]: (state: OrderManagerState) => {
    return update(state, {
      isSubmittingOrderChanges: { $set: false },
      showReviewBeforeSubmitView: { $set: false },
      submitOrderChangesInfo: {
        $set: {
          success: true,
        },
      },
    })
  },
  [actionFail(actions.SUBMIT_ORDER_CHANGES)]: (
    state: OrderManagerState,
    action: AxiosAction<
      ReviewOrderChangesResponse,
      ActionCreatorTypes["submitOrderChanges"]
    >,
  ) => {
    return update(state, {
      isSubmittingOrderChanges: { $set: false },
      submitOrderChangesInfo: {
        $set: {
          success: false,
          error: action?.error?.response?.data,
        },
      },
    })
  },
  [actions.MARK_ORDER]: (state: OrderManagerState) => {
    return update(state, {
      isMarkingOrder: { $set: true },
    })
  },
  [actionSuccess(actions.MARK_ORDER)]: (state: OrderManagerState) => {
    return update(state, {
      isMarkingOrder: { $set: false },
    })
  },
  [actionFail(actions.MARK_ORDER)]: (state: OrderManagerState) => {
    return update(state, {
      isMarkingOrder: { $set: false },
    })
  },
  [actions.UPDATE_WAITING_MARK_ORDER]: (
    state: OrderManagerState,
    action: ActionCreatorTypes["updateWaitingMarkOrder"],
  ) => {
    return update(state, {
      isWaitingMarkOrder: {
        $set: !!action?.payload.wait,
      },
    })
  },
  [actions.NEW_ORDER_NOTIFICATION]: (
    state: OrderManagerState,
    action: ActionCreatorTypes["newOrderNotification"],
  ) => {
    const { data } = action?.payload

    return update(state, {
      newOrderNotification: { $set: data },
      orderNotificationList: { $push: [data] },
      orderNotificationCount: { $set: state.orderNotificationCount + 1 },
    })
  },
  [actions.CHANGE_ORDER_NOTIFICATION_COUNT]: (
    state: OrderManagerState,
    action: ActionCreatorTypes["changeOrderNotificationCount"],
  ) => {
    return update(state, {
      orderNotificationCount: { $set: action?.payload.count },
    })
  },
  [actions.REMOVE_ORDER_FROM_NOTIFICATION_LIST]: (
    state: OrderManagerState,
    action: ActionCreatorTypes["removeOrderFromNotificationList"],
  ) => {
    const newList = state.orderNotificationList?.filter(
      order => order.orderID !== action?.payload.orderID,
    )
    return update(state, {
      orderNotificationList: { $set: newList },
    })
  },
  [actions.UPDATE_ORDER_MANAGER_CONFIG]: (
    state: OrderManagerState,
    action: ActionCreatorTypes["updateOrderManagerConfig"],
  ) => {
    return update(state, {
      orderManagerConfig: {
        $set: action?.payload.config,
      },
    })
  },
  [actions.UPDATE_OPENED_ORDER_DETAIL]: (
    state: OrderManagerState,
    action: ActionCreatorTypes["updateOpenedOrderDetail"],
  ) => {
    return update(state, {
      openedOrderDetail: {
        $set: action?.payload.openedOrderDetail,
      },
      showReviewBeforeSubmitView: { $set: false },
    })
  },
})
